import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Button,
  Divider,
  MenuItem,
  Alert
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import PersonaPicker from '../../shared/pickers/PersonaPicker';
import ToolPicker from '../../shared/pickers/ToolPicker';
import AssessmentPicker from '../../shared/pickers/AssessmentPicker';

const TRANSITION_TYPES = [
  { value: 'tool_success', label: 'Tool Success' },
  { value: 'assessment_complete', label: 'Assessment Complete' },
  { value: 'manual', label: 'Manual Progression' },
  { value: 'time_based', label: 'Time-Based' }
];

const StageEditorPanel = ({ stage, onStageChange, onClose }) => {
  const [localStage, setLocalStage] = useState(stage);
  const [hasChanges, setHasChanges] = useState(false);
  // Track score ranges for each assessment by index
  const [assessmentScoreRanges, setAssessmentScoreRanges] = useState({});

  useEffect(() => {
    setLocalStage(stage);
    setHasChanges(false);
    setAssessmentScoreRanges({});
  }, [stage]);

  const handleChange = (field, value) => {
    setLocalStage(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTransitionChange = (field, value) => {
    setLocalStage(prev => ({
      ...prev,
      transitionTrigger: { ...prev.transitionTrigger, [field]: value }
    }));
    setHasChanges(true);
  };

  const handleToolsChange = (toolIds) => {
    const tools = toolIds.map(toolId => ({
      toolId,
      enabled: true,
      customConfig: {}
    }));
    handleChange('availableTools', tools);
  };

  const handleAddAssessment = () => {
    const newAssessments = [
      ...(localStage.requiredAssessments || []),
      {
        assessmentKey: '',
        passingCriteria: {
          minScore: null,  // Will be set when assessment is selected
          maxScore: null   // Will be set when assessment is selected
        },
        failureAction: {
          type: 'block'
        }
      }
    ];
    handleChange('requiredAssessments', newAssessments);
  };

  const handleRemoveAssessment = (index) => {
    const newAssessments = localStage.requiredAssessments.filter((_, i) => i !== index);
    handleChange('requiredAssessments', newAssessments);
  };

  const handleAssessmentChange = (index, field, value, scoreRange = null) => {
    const newAssessments = [...localStage.requiredAssessments];
    if (field.startsWith('passingCriteria.')) {
      const criteriaField = field.split('.')[1];
      newAssessments[index].passingCriteria = {
        ...newAssessments[index].passingCriteria,
        [criteriaField]: value
      };
    } else {
      newAssessments[index][field] = value;
      // When assessmentKey changes and we have a score range, set default values
      if (field === 'assessmentKey' && scoreRange) {
        newAssessments[index].passingCriteria = {
          ...newAssessments[index].passingCriteria,
          minScore: scoreRange.min,
          maxScore: scoreRange.max
        };
      }
    }
    handleChange('requiredAssessments', newAssessments);
  };

  // Memoized function to set score range display info only (no stage modification)
  const setScoreRangeForIndex = useCallback((index, assessment) => {
    if (assessment && assessment.scoringBands && assessment.scoringBands.length > 0) {
      const minScore = Math.min(...assessment.scoringBands.map(b => b.range[0]));
      const maxScore = Math.max(...assessment.scoringBands.map(b => b.range[1]));
      setAssessmentScoreRanges(prev => ({
        ...prev,
        [index]: { min: minScore, max: maxScore, name: assessment.name }
      }));
    }
  }, []);

  // Handle when assessment is selected by user - update score range AND stage data
  const handleAssessmentSelect = (index, assessment, isInitialLoad = false) => {
    if (assessment && assessment.scoringBands && assessment.scoringBands.length > 0) {
      // Calculate min and max from scoring bands
      const minScore = Math.min(...assessment.scoringBands.map(b => b.range[0]));
      const maxScore = Math.max(...assessment.scoringBands.map(b => b.range[1]));

      // Store the score range for this assessment
      setAssessmentScoreRanges(prev => ({
        ...prev,
        [index]: { min: minScore, max: maxScore, name: assessment.name }
      }));

      // Only update the assessment's passing criteria if this is a user selection (not initial load)
      if (!isInitialLoad) {
        const newAssessments = [...localStage.requiredAssessments];
        newAssessments[index].passingCriteria = {
          ...newAssessments[index].passingCriteria,
          minScore: minScore,
          maxScore: maxScore
        };
        handleChange('requiredAssessments', newAssessments);
      }
    } else {
      // Clear the score range for this assessment
      setAssessmentScoreRanges(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  // Validate stage configuration
  const isStageValid = () => {
    // Stage name is required
    if (!localStage.stageName?.trim()) return false;

    // Persona is required
    if (!localStage.personaId) return false;

    // Validate transition configuration based on type
    const transitionType = localStage.transitionTrigger?.type;
    // tool_success doesn't require toolName - it's optional
    if (transitionType === 'time_based') {
      if (!localStage.transitionTrigger?.delayMinutes || localStage.transitionTrigger.delayMinutes <= 0) return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!isStageValid()) return;
    onStageChange(localStage);
    setHasChanges(false);
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 450 } }
      }}
      sx={{ zIndex: 1400 }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Edit Stage {localStage.stageNumber}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {hasChanges && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have unsaved changes
          </Alert>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
          <TextField
            fullWidth
            label="Stage Name"
            value={localStage.stageName}
            onChange={(e) => handleChange('stageName', e.target.value)}
            sx={{ mb: 2 }}
            required
          />

          <PersonaPicker
            value={localStage.personaId}
            onChange={(value) => handleChange('personaId', value)}
            label="Stage Persona *"
            required
            error={!localStage.personaId}
            helperText={!localStage.personaId ? 'Stage persona is required' : 'AI persona that guides this stage'}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Available Tools
          </Typography>
          <ToolPicker
            value={(localStage.availableTools || []).map(t => t.toolId)}
            onChange={handleToolsChange}
            label="Select Tools"
            helperText="Tools the AI can use during this stage"
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">
              Required Assessments
            </Typography>
            <Button startIcon={<Add />} onClick={handleAddAssessment} size="small">
              Add
            </Button>
          </Box>

          {(localStage.requiredAssessments || []).map((assessment, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Assessment {index + 1}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveAssessment(index)} color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>

              <AssessmentPicker
                value={assessment.assessmentKey}
                onChange={(value, selectedAssessment) => {
                  // Get score range if assessment has scoring bands
                  let scoreRange = null;
                  if (selectedAssessment?.scoringBands?.length > 0) {
                    const minScore = Math.min(...selectedAssessment.scoringBands.map(b => b.range[0]));
                    const maxScore = Math.max(...selectedAssessment.scoringBands.map(b => b.range[1]));
                    scoreRange = { min: minScore, max: maxScore };
                    // Also update display score range
                    setScoreRangeForIndex(index, selectedAssessment);
                  }
                  handleAssessmentChange(index, 'assessmentKey', value, scoreRange);
                }}
                onAssessmentSelect={(selectedAssessment) => setScoreRangeForIndex(index, selectedAssessment)}
                label="Assessment"
                required
              />

              {assessmentScoreRanges[index] && (
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                  Valid score range: {assessmentScoreRanges[index].min} - {assessmentScoreRanges[index].max}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  type="number"
                  label="Min Score"
                  value={assessment.passingCriteria?.minScore ?? ''}
                  onChange={(e) => handleAssessmentChange(index, 'passingCriteria.minScore', e.target.value === '' ? null : Number(e.target.value))}
                  size="small"
                  fullWidth
                  placeholder={assessmentScoreRanges[index] ? String(assessmentScoreRanges[index].min) : 'Select assessment first'}
                  inputProps={{
                    min: assessmentScoreRanges[index]?.min ?? 0,
                    max: assessmentScoreRanges[index]?.max ?? 100
                  }}
                  helperText={assessmentScoreRanges[index] ? `Valid: ${assessmentScoreRanges[index].min}` : 'Select assessment first'}
                />
                <TextField
                  type="number"
                  label="Max Score"
                  value={assessment.passingCriteria?.maxScore ?? ''}
                  onChange={(e) => handleAssessmentChange(index, 'passingCriteria.maxScore', e.target.value === '' ? null : Number(e.target.value))}
                  size="small"
                  fullWidth
                  placeholder={assessmentScoreRanges[index] ? String(assessmentScoreRanges[index].max) : 'Select assessment first'}
                  inputProps={{
                    min: assessmentScoreRanges[index]?.min ?? 0,
                    max: assessmentScoreRanges[index]?.max ?? 100
                  }}
                  helperText={assessmentScoreRanges[index] ? `Valid: ${assessmentScoreRanges[index].max}` : 'Select assessment first'}
                />
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Transition Configuration
          </Typography>

          <TextField
            fullWidth
            select
            label="Transition Type"
            value={localStage.transitionTrigger?.type || 'tool_success'}
            onChange={(e) => handleTransitionChange('type', e.target.value)}
            sx={{ mb: 2 }}
            required
            SelectProps={{
              MenuProps: {
                sx: { zIndex: 1500 }  // Higher than Drawer's zIndex (1400)
              }
            }}
          >
            {TRANSITION_TYPES.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {localStage.transitionTrigger?.type === 'tool_success' && (
            <Box sx={{ mb: 2 }}>
              <ToolPicker
                value={localStage.transitionTrigger?.toolName || ''}
                onChange={(toolName) => handleTransitionChange('toolName', toolName)}
                label="Transition Tool"
                helperText="Tool that triggers transition to next stage (optional)"
                multiple={false}
              />
            </Box>
          )}

          {localStage.transitionTrigger?.type === 'time_based' && (
            <TextField
              fullWidth
              type="number"
              label="Delay (minutes) *"
              value={localStage.transitionTrigger?.delayMinutes || ''}
              onChange={(e) => handleTransitionChange('delayMinutes', Number(e.target.value))}
              sx={{ mb: 2 }}
              required
              error={!localStage.transitionTrigger?.delayMinutes || localStage.transitionTrigger?.delayMinutes <= 0}
              helperText={
                !localStage.transitionTrigger?.delayMinutes || localStage.transitionTrigger?.delayMinutes <= 0
                  ? 'Delay must be greater than 0 minutes'
                  : 'Time to wait before transitioning to next stage'
              }
              inputProps={{ min: 1 }}
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Greeting Message"
            value={localStage.greetingMessage || ''}
            onChange={(e) => handleChange('greetingMessage', e.target.value)}
            helperText="Message shown when entering this stage"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Transition Message"
            value={localStage.transitionMessage || ''}
            onChange={(e) => handleChange('transitionMessage', e.target.value)}
            helperText="Message shown when moving to next stage"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={onClose} fullWidth variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} fullWidth variant="contained" disabled={!hasChanges || !isStageValid()}>
            Save Changes
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default StageEditorPanel;
